import { z } from 'zod';
import { NOTE_COLORS } from '../colors.js';
import { LIMITS } from '../limits.js';
import { labelNameSchema } from './labels.js';
import { NOTE_TYPES } from './notes.js';

const isoDate = z.iso.datetime({ offset: true });

/** Note à importer depuis une autre application (Google Keep…), dans un format neutre. */
export const importNoteSchema = z.object({
  /**
   * Identifiant stable dans la source (ex. `keep:<horodatage de création>`).
   * Une note déjà importée avec le même identifiant est ignorée : l'import peut être relancé sans doublon.
   */
  externalId: z.string().trim().min(1).max(200),
  title: z.string().max(LIMITS.noteTitle).default(''),
  content: z.string().max(LIMITS.noteContent).default(''),
  type: z.enum(NOTE_TYPES).default('text'),
  items: z
    .array(
      z.object({
        text: z.string().max(LIMITS.checklistItemText),
        checked: z.boolean().default(false),
      }),
    )
    .max(LIMITS.checklistItems)
    .default([]),
  color: z.enum(NOTE_COLORS).default('default'),
  /** Noms des libellés : créés s'ils n'existent pas encore. */
  labels: z.array(labelNameSchema).max(50).default([]),
  pinned: z.boolean().default(false),
  archived: z.boolean().default(false),
  trashed: z.boolean().default(false),
  createdAt: isoDate.optional(),
  updatedAt: isoDate.optional(),
});
export type ImportNoteInput = z.input<typeof importNoteSchema>;

export const importNotesSchema = z.object({
  notes: z.array(importNoteSchema).max(LIMITS.importBatch).default([]),
  /** Libellés à créer même s'ils ne sont portés par aucune note (Labels.txt de Takeout). */
  labels: z.array(labelNameSchema).max(LIMITS.labelsPerUser).default([]),
});
export type ImportNotesInput = z.input<typeof importNotesSchema>;

export interface ImportNoteResultDto {
  externalId: string;
  noteId: string;
  /** `skipped` : déjà importée lors d'un précédent import. */
  status: 'created' | 'skipped';
  attachmentCount: number;
}

export interface ImportNotesResultDto {
  results: ImportNoteResultDto[];
  createdLabels: number;
}
